import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';
import { Provider } from 'react-redux';
import { persistor, store } from './store';
import { PersistGate } from 'redux-persist/integration/react';
import * as Sentry from '@sentry/react';
import { AuthProvider } from './context/AuthProvider';
import AppErrorBoundary from './components/ErrorBoundary';
import {
  getTenantIdFromHostname,
  getTenantConfig,
  isValidTenantId,
} from './config/tenants';
import { setTenantId } from './store/slices/clientSlice';

if (
  import.meta.env.VITE_APP_ENV === 'production' &&
  import.meta.env.VITE_PUBLIC_SENTRY_DSN
) {
  Sentry.init({
    dsn: import.meta.env.VITE_PUBLIC_SENTRY_DSN,
    environment: import.meta.env.VITE_APP_ENV,
    release: import.meta.env.VITE_APP_VERSION || '1.0.0',
    dist:
      import.meta.env.VITE_APP_ENV === 'production' ? 'production' : 'sandbox',
  });
}
// Resolve tenant: hostname first → env var fallback (devstack/localhost only)
// No default fallback — an unresolvable tenant must crash rather than silently
// render the wrong brand to a client.
const envTenantId = import.meta.env.VITE_TENANT_ID;
const isNonProdEnv = import.meta.env.VITE_APP_ENV !== 'production';
const tenantId =
  getTenantIdFromHostname() ||
  (isNonProdEnv && isValidTenantId(envTenantId) ? envTenantId : undefined);

if (!tenantId) {
  throw new Error(
    '[LMS] Could not resolve a tenant for this hostname. ' +
      'Add a hostname entry to hostname-to-tenant-map or set VITE_TENANT_ID for local development.',
  );
}
store.dispatch(setTenantId(tenantId));

const activeTenantConfig = getTenantConfig(tenantId);
document.title = activeTenantConfig.tenantName;
const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
if (favicon) {
  favicon.href = activeTenantConfig.faviconUrl;
}

const rootElement = document.getElementById('root');

if (rootElement) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <AppErrorBoundary>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <AuthProvider>
            <App />
          </AuthProvider>
        </PersistGate>
      </Provider>
    </AppErrorBoundary>,
  );
}
